import React, { useState, useMemo, useEffect, Suspense, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Select as MuiSelect,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
  Typography,
  Fade,
  Grow,
  Checkbox,
  ListItemText,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faXmark,
  faChevronDown,
  faCircleExclamation,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import MiniLoader from './MiniLoader';
import * as Yup from 'yup';
import ApiServices from '../Services/ApiServices';
import FileUploader from './FileUploader';
import StyledText from './StyledText';
import { useLanguage } from '../../contexts/LanguageContext';

const TimeComponent = React.lazy(() => import('./TimeComponent'));
const DateComponent = React.lazy(() => import('./DateComponent'));

const ZAIN = "'Zain', sans-serif";
const PRIMARY_GRADIENT = 'linear-gradient(135deg, #7a6ad8 0%, #9d8df1 100%)';
const PRIMARY_COLOR = '#7a6ad8';

const zainOverrideSx = {
  fontFamily: ZAIN,
  '&, & *': {
    fontFamily: `${ZAIN} !important`,
  }
};

// ─── Shared Styles ───────────────────────────────────────────────────────────

const getRtlSx = (isRTL) => ({
  fontFamily: ZAIN,
  direction: isRTL ? 'rtl' : 'ltr',
  textAlign: isRTL ? 'right' : 'left'
});

const getInputLabelSx = (isRTL) => ({
  fontFamily: ZAIN,
  color: '#64748b',
  '&.Mui-focused': { color: PRIMARY_COLOR },
  ...(isRTL ? {
    right: 28,
    left: 'unset',
    transformOrigin: 'right',
    '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
  } : {
    left: 28,
    right: 'unset',
    transformOrigin: 'left',
    '&.MuiInputLabel-shrink': { transform: 'translate(-14px, -9px) scale(0.75)' },
  }),
});

const getCommonInputSx = (isRTL) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: ZAIN,
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    minHeight: '52px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      borderColor: '#e2e8f0',
      textAlign: isRTL ? 'right' : 'left',
    },
    '&:hover fieldset': {
      borderColor: '#cbd5e1',
    },
    '&.Mui-focused fieldset': {
      borderColor: PRIMARY_COLOR,
      borderWidth: '2px',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 4px 12px rgba(122, 106, 216, 0.1)',
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px 14px',
    fontFamily: ZAIN,
    textAlign: isRTL ? 'right' : 'left',
    direction: isRTL ? 'rtl' : 'ltr',
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    textAlign: isRTL ? 'right' : 'left',
    marginRight: isRTL ? '10px' : 'unset',
    marginLeft: isRTL ? 'unset' : '10px',
  }
});

const getSelectSx = (isRTL) => ({
  ...getCommonInputSx(isRTL),
  '& .MuiSelect-icon': {
    left: isRTL ? 12 : 'unset',
    right: isRTL ? 'unset' : 12,
    color: '#94a3b8',
    transition: 'transform 0.2s ease',
  },
  '& .MuiSelect-select': {
    paddingLeft: isRTL ? '40px !important' : '14px !important',
    paddingRight: isRTL ? '14px !important' : '40px !important',
  },
});

// ─── Components ─────────────────────────────────────────────────────────────

const FieldLabel = ({ field }) => {
  if (!field?.icon) return <span style={{ fontFamily: ZAIN, fontWeight: 500 }}>{field.label}</span>;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontFamily: ZAIN, direction: field.direction || 'rtl' }}>
      <FontAwesomeIcon icon={field.icon} style={{ color: PRIMARY_COLOR, fontSize: '0.9rem' }} />
      <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit' }}>{field.label}</Typography>
    </Box>
  );
};

const normalizeAsyncOptions = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;

  const items =
    payload?.items ||
    payload?.data?.items ||
    payload?.data_list ||
    payload?.result ||
    payload?.data ||
    [];

  return Array.isArray(items) ? items : [];
};

const resolveFieldBoolean = (value, values) => (typeof value === 'function' ? value(values) : value);

const resolveFieldText = (value, values) => {
  const resolved = typeof value === 'function' ? value(values) : value;
  return resolved ? String(resolved) : '';
};

const isFieldDisabled = (field, values) => Boolean(field?.lockedValue) || Boolean(resolveFieldBoolean(field?.disabled, values));

const isFieldRequired = (field, values) => Boolean(resolveFieldBoolean(field?.required, values));

// ─── Yup schema builder ───────────────────────────────────────────────────────

const buildYupSchema = (formConfig, values = {}, t) => {
  const shape = {};

  formConfig.forEach((field) => {
    const { name, type, label, requiredMessage, min, max, minMessage, maxMessage, pattern, patternMessage, validation } = field;
    const fieldDisabled = isFieldDisabled(field, values);
    const fieldRequired = isFieldRequired(field, values);

    if (fieldDisabled) {
      shape[name] = Yup.mixed().nullable().optional();
      return;
    }

    let v = validation;

    if (!v) {
      switch (type) {
        case 'email': v = Yup.string().email(t('invalidEmail') || 'البريد الإلكتروني غير صالح'); break;
        case 'number':
          v = Yup.number().typeError(t('mustBeNumeric') || 'يجب أن تكون قيمة رقمية');
          if (min !== undefined) v = v.min(min, minMessage || `${t('minimumIs') || 'الحد الأدنى هو'} ${min}`);
          if (max !== undefined) v = v.max(max, maxMessage || `${t('maximumIs') || 'الحد الأقصى هو'} ${max}`);
          break;
        case 'url': v = Yup.string().url(t('invalidUrl') || 'الرابط غير صالح'); break;
        case 'tel':
          v = Yup.string().matches(pattern || /^[0-9]{10,15}$/, patternMessage || t('invalidPhoneNumber') || 'رقم الهاتف غير صالح');
          break;
        case 'select':
        case 'async-select':
          v = Yup.string();
          break;
        case 'multi-select':
          v = Yup.array().of(Yup.mixed());
          break;
        case 'file': v = Yup.mixed(); break;
        default:
          v = Yup.string();
          if (min !== undefined) v = v.min(min, minMessage || `${t('minChars') || 'الحد الأدنى'} ${min}`);
          if (max !== undefined) v = v.max(max, maxMessage || `${t('maxChars') || 'الحد الأقصى'} ${max}`);
          if (pattern) v = v.matches(pattern, patternMessage || t('invalidFormat') || 'الصيغة غير صحيحة');
      }
    }

    shape[name] = fieldRequired
      ? (type === 'multi-select'
          ? v.min(1, requiredMessage || `${label} ${t('requiredField') || 'مطلوب'}`)
          : v.required(requiredMessage || `${label} ${t('requiredField') || 'مطلوب'}`))
      : v.nullable().optional();
  });

  return Yup.object().shape(shape);
};

// ─── AsyncSelectField ─────────────────────────────────────────────────────────

const AsyncSelectField = ({ field, value, onChange, onBlur, error, disabled = false }) => {
  const { t } = useLanguage();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const normalizedValue = value === null || value === undefined ? '' : String(value);

  const isRTL = field.direction !== 'ltr';
  const commonInputSx = getCommonInputSx(isRTL);
  const selectSx = getSelectSx(isRTL);
  const inputLabelSx = getInputLabelSx(isRTL);
  const rtlSx = getRtlSx(isRTL);

  const mapItem = useCallback((item) => {
    if (field.optionsMap) {
      return field.optionsMap(item);
    }
    return {
      value: String(item.id),
      label: item.name ?? item.full_name ?? item.label ?? String(item.id),
    };
  }, [field.optionsMap]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await ApiServices.get(field.optionsUrl);
        if (cancelled) return;
        const items = normalizeAsyncOptions(res);
        let mapped = (field.initialLimit ? items.slice(0, field.initialLimit) : items).map(mapItem);
        if (field.lockedValue) {
          const exists = mapped.some((o) => String(o.value) === String(field.lockedValue));
          if (!exists) {
            const match = items.find((i) => String(i.id) === String(field.lockedValue));
            if (match) {
              mapped = [mapItem(match), ...mapped];
            } else if (field.lockedLabel) {
              // If the parent already knew the label, inject it so the display works
              mapped = [{ value: String(field.lockedValue), label: field.lockedLabel }, ...mapped];
            }
          }
        }

        // Keep the current selected value visible even if it is not in teacher lookup list.
        if (normalizedValue) {
          const exists = mapped.some((o) => String(o.value) === normalizedValue);
          if (!exists) {
            const match = items.find((i) => String(i.id) === normalizedValue);
            if (match) {
              mapped = [mapItem(match), ...mapped];
            } else if (field.valueLabel) {
              mapped = [{ value: normalizedValue, label: String(field.valueLabel) }, ...mapped];
            }
          }
        }

        mapped = mapped.filter(
          (opt, idx, arr) => arr.findIndex((x) => String(x.value) === String(opt.value)) === idx
        );
        setOptions(mapped);
      } catch (e) {
        
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [field.optionsUrl, field.lockedValue, field.lockedLabel, field.valueLabel, normalizedValue, field.initialLimit, mapItem]);

  useEffect(() => {
    if (!field.searchUrl || field.lockedValue) return;
    let cancelled = false;

    if (!searchTerm.trim()) {
      (async () => {
        try {
          setLoading(true);
          const res = await ApiServices.get(field.optionsUrl);
          if (cancelled) return;
          const items = normalizeAsyncOptions(res);
          setOptions((field.initialLimit ? items.slice(0, field.initialLimit) : items).map(mapItem));
        } finally { if (!cancelled) setLoading(false); }
      })();
      return () => { cancelled = true; };
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await ApiServices.get(`${field.searchUrl}${encodeURIComponent(searchTerm.trim())}`);
        if (cancelled) return;
        const items = normalizeAsyncOptions(res);
        setOptions(items.map(mapItem));
      } catch (e) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchTerm, field.optionsUrl, field.searchUrl, field.lockedValue, field.initialLimit, mapItem]);

  const isLocked = Boolean(field.lockedValue) || disabled;

  return (
    <FormControl
      fullWidth
      error={error}
      size="small"
      sx={{ direction: field.direction || 'rtl', textAlign: field.direction === 'ltr' ? 'left' : 'right' }}
    >
      <InputLabel
        sx={{
          ...inputLabelSx,
          ...(field.direction === 'ltr'
            ? { left: 14, right: 'unset', transformOrigin: 'left' }
            : {}),
        }}
      >
        <FieldLabel field={field} />
      </InputLabel>
      <MuiSelect
        value={normalizedValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        label={field.label}
        disabled={isLocked || loading}
        IconComponent={() => <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.8rem', marginLeft: 12, color: '#94a3b8' }} />}
        sx={{
          ...selectSx,
          ...(field.direction === 'ltr'
            ? {
                '& .MuiSelect-select': {
                  paddingLeft: '14px !important',
                  paddingRight: '40px !important',
                  textAlign: 'left',
                  direction: 'ltr',
                },
                '& .MuiSelect-icon': { right: 12, left: 'unset' },
              }
            : {}),
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
          PaperProps: {
            sx: {
              ...zainOverrideSx,
              maxHeight: 300,
              borderRadius: '12px',
              mt: 1,
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              direction: field.direction || 'rtl',
              textAlign: field.direction === 'ltr' ? 'left' : 'right'
            },
          },
        }}
      >
        {loading && (
          <MenuItem disabled sx={{ ...rtlSx, direction: field.direction || 'rtl', textAlign: field.direction === 'ltr' ? 'left' : 'right' }}>
            <MiniLoader size={14} color={PRIMARY_COLOR} speed="normal" />
            <Box component="span" sx={{ marginRight: 1.5 }}>{t('loadingOptions') || 'جاري التحميل...'}</Box>
          </MenuItem>
        )}

        {!loading && field.searchUrl && !isLocked && (
          <Box sx={{ p: 1.5, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('searchPlaceholder') || "ابحث..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              variant="outlined"
              sx={commonInputSx}
                inputProps={{
                  autoComplete: 'new-password',
                  style: {
                    direction: field.direction || 'rtl',
                    textAlign: field.direction === 'ltr' ? 'left' : 'right',
                  },
                }}
            />
          </Box>
        )}

        {!loading && options.length === 0 && (
          <MenuItem
            disabled
            sx={{ ...rtlSx, py: 2, direction: field.direction || 'rtl', textAlign: field.direction === 'ltr' ? 'left' : 'right' }}
          >
            {t('noResultsFound') || 'لا توجد نتائج'}
          </MenuItem>
        )}

        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{
              ...getRtlSx(field.direction === 'rtl'),
              direction: field.direction || 'rtl',
              textAlign: field.direction === 'ltr' ? 'left' : 'right',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};

// ─── SelectField ─────────────────────────────────────────────────────────────

const SelectField = ({ field, value, onChange, onBlur, error, disabled = false }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const normalizedValue = value === null || value === undefined ? '' : String(value);

  const isRTL = field.direction !== 'ltr';
  const commonInputSx = getCommonInputSx(isRTL);
  const selectSx = getSelectSx(isRTL);
  const inputLabelSx = getInputLabelSx(isRTL);
  const rtlSx = getRtlSx(isRTL);

  const isLocked = Boolean(field.lockedValue) || disabled;

  const options = (field.options || []).filter((opt) => {
    if (!field.searchable || !searchTerm.trim()) return true;
    return opt.label?.toString().toLowerCase().includes(searchTerm.toLowerCase().trim());
  });

  return (
    <FormControl
      fullWidth
      error={error}
      size="small"
      sx={{ direction: field.direction || 'rtl', textAlign: field.direction === 'ltr' ? 'left' : 'right' }}
    >
      <InputLabel
        sx={{
          ...inputLabelSx,
          ...(field.direction === 'ltr'
            ? { left: 14, right: 'unset', transformOrigin: 'left' }
            : {}),
        }}
      >
        <FieldLabel field={field} />
      </InputLabel>
      <MuiSelect
        value={normalizedValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        label={field.label}
        disabled={isLocked}
        IconComponent={() => <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.8rem', marginLeft: 12, color: '#94a3b8' }} />}
        sx={{
          ...selectSx,
          ...(field.direction === 'ltr'
            ? {
                '& .MuiSelect-select': {
                  paddingLeft: '14px !important',
                  paddingRight: '40px !important',
                  textAlign: 'left',
                  direction: 'ltr',
                },
                '& .MuiSelect-icon': { right: 12, left: 'unset' },
              }
            : {}),
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
          PaperProps: {
            sx: {
              ...zainOverrideSx,
              maxHeight: 300,
              borderRadius: '12px',
              mt: 1,
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              direction: field.direction || 'rtl',
              textAlign: field.direction === 'ltr' ? 'left' : 'right'
            },
          },
        }}
      >
        {field.searchable && !isLocked && (
          <Box sx={{ p: 1.5, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('searchPlaceholder') || "ابحث..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              variant="outlined"
              sx={commonInputSx}
              inputProps={{
                autoComplete: 'new-password',
                style: {
                  direction: field.direction || 'rtl',
                  textAlign: field.direction === 'ltr' ? 'left' : 'right',
                },
              }}
            />
          </Box>
        )}

        {options.length === 0 && (
          <MenuItem
            disabled
            sx={{ ...rtlSx, py: 2, direction: field.direction || 'rtl', textAlign: field.direction === 'ltr' ? 'left' : 'right' }}
          >
            {t('noResultsFound') || 'لا توجد نتائج'}
          </MenuItem>
        )}

        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{
              ...getRtlSx(field.direction === 'rtl'),
              direction: field.direction || 'rtl',
              textAlign: field.direction === 'ltr' ? 'left' : 'right',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};

// ─── FileField ────────────────────────────────────────────────────────────────

const FileField = ({ field, value, onChange, onBlur, error, disabled = false }) => {
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null);
  const [imageDragOver, setImageDragOver] = useState(false);

  const applyFile = (files) => {
    if (disabled) return;
    if (!files) return;
    
    if (field.uploadMultipleFile) {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;
      onChange(fileArray);
      setPreview(null); // FileUploader handles multiple previews internally
      onBlur();
    } else {
      const file = files[0];
      if (!file) return;
      onChange(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
      onBlur();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        direction: field.direction || 'rtl',
        textAlign: field.direction === 'ltr' ? 'left' : 'right',
      }}
    >
      <Typography sx={{ mb: 1.5, fontFamily: ZAIN, fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>
        <FieldLabel field={field} />
      </Typography>
      <FileUploader
        previewUrl={preview}
        onFileChange={(e) => applyFile(e.currentTarget.files)}
        onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setImageDragOver(false); }}
        onDrop={(e) => { e.preventDefault(); setImageDragOver(false); applyFile(e.dataTransfer.files); }}
        imageDragOver={imageDragOver}
        accept={field.accept || 'image/*'}
        placeholder={field.label}
        subText={field.subText || t('dragAndDropFile') || 'اسحب وأفلت الملف هنا أو اضغط للاختيار'}
        buttonText={t('chooseFile') || "اختيار ملف"}
        changeButtonText={t('changeFile') || "تغيير الملف"}
        fileName={value?.name || null}
        id={field.name}
        disabled={disabled}
        uploadMultipleFile={field.uploadMultipleFile}
        MaxFileSize={field.MaxFileSize || field.maxFileSize || 10}
      />
    </Box>
  );
};

// ─── DynamicModal ─────────────────────────────────────────────────────────────

const DynamicModal = ({
  isOpen,
  onClose,
  apiUrl,
  formConfig,
  title,
  submitButtonText,
  multipart = false,
  onSuccess,
  onSubmit,
  onError,
  direction,
}) => {
  const { t, language } = useLanguage();
  const resolvedDirection = direction || (language === 'ar' ? 'rtl' : 'ltr');
  const isRTL = resolvedDirection === 'rtl';

  const dynamicRtlSx = useMemo(() => getRtlSx(isRTL), [isRTL]);
  const dynamicCommonInputSx = useMemo(() => getCommonInputSx(isRTL), [isRTL]);
  const dynamicSelectSx = useMemo(() => getSelectSx(isRTL), [isRTL]);
  const dynamicInputLabelSx = useMemo(() => getInputLabelSx(isRTL), [isRTL]);

  const initData = useCallback(() =>
    formConfig.reduce((acc, f) => {
      acc[f.name] = f.lockedValue ?? f.defaultValue ?? '';
      return acc;
    }, {}), [formConfig]);

  const [formData, setFormData] = useState(initData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData(initData());
      setErrors({});
      setTouched({});
      setPasswordVisibility({});
    }
  }, [isOpen, initData]);

  const validationSchema = useMemo(() => buildYupSchema(formConfig, formData, t), [formConfig, formData, t]);

  useEffect(() => {
    setErrors((previousErrors) => {
      let changed = false;
      const nextErrors = { ...previousErrors };

      formConfig.forEach((field) => {
        if (isFieldDisabled(field, formData) && nextErrors[field.name]) {
          delete nextErrors[field.name];
          changed = true;
        }
      });

      return changed ? nextErrors : previousErrors;
    });
  }, [formConfig, formData]);

  const validateField = async (name, value) => {
    try {
      await validationSchema.validateAt(name, { ...formData, [name]: value });
      setErrors((p) => ({ ...p, [name]: null }));
    } catch (err) {
      setErrors((p) => ({ ...p, [name]: err.message }));
    }
  };

  const handleChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
    if (touched[name]) validateField(name, value);
  };

  const handleBlur = (name, valueOverride) => {
    setTouched((p) => ({ ...p, [name]: true }));
    const isEventLikeValue =
      valueOverride &&
      typeof valueOverride === 'object' &&
      (valueOverride.target || valueOverride.currentTarget || valueOverride.nativeEvent);

    const safeValue = isEventLikeValue ? formData[name] : (valueOverride ?? formData[name]);
    validateField(name, safeValue);
  };

  const validateAll = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const e = {};
      if (Array.isArray(err?.inner) && err.inner.length > 0) {
        err.inner.forEach((i) => {
          if (i?.path) e[i.path] = i.message;
        });
      } else if (err?.path) {
        e[err.path] = err.message;
      }
      setErrors(e);
      setTouched(formConfig.reduce((acc, field) => {
        acc[field.name] = true;
        return acc;
      }, {}));
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!(await validateAll())) return;
    setLoading(true);
    try {
      if (onSubmit) {
        await onSubmit({ ...formData });
      } else if (multipart) {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (Array.isArray(v)) {
            v.forEach((item) => {
              if (item !== null && item !== undefined && item !== '') fd.append(k, item);
            });
            return;
          }

          if (v !== null && v !== undefined && v !== '') fd.append(k, v);
        });
        await ApiServices.post(apiUrl, fd, true);
      } else {
        await ApiServices.post(apiUrl, { ...formData });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const fieldWithDirection = { ...field, direction: resolvedDirection };
    const value = formData[field.name] ?? '';
    const fieldError = touched[field.name] && !!errors[field.name];
    const helperText = touched[field.name] ? errors[field.name] || '' : '';
    const fieldDisabled = isFieldDisabled(field, formData);
    const helperNote = resolveFieldText(field.helperNote, formData);
    const renderNote = !fieldError && Boolean(helperNote);

    switch (field.type) {
      case 'styledText':
        return (
          <FormControl fullWidth error={fieldError} sx={{ direction: resolvedDirection, mb: 1, ...dynamicRtlSx }}>
             <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <FieldLabel field={fieldWithDirection} />
             </Box>
             <StyledText
                value={value}
                onChange={(content) => handleChange(field.name, content)}
                placeholder={field.placeholder || ''}
                readOnly={fieldDisabled}
                height={field.height || 400}
             />
             {helperText && (
               <FormHelperText error sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                 <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                 {helperText}
               </FormHelperText>
             )}
             {renderNote && (
               <FormHelperText
                 sx={{
                   ...dynamicRtlSx,
                   mt: 0.75,
                   px: 1,
                   py: 0.5,
                   borderRadius: '8px',
                   color: '#854d0e',
                   backgroundColor: '#fef3c7',
                   border: '1px solid #fde68a',
                   fontWeight: 700,
                 }}
               >
                 {helperNote}
               </FormHelperText>
             )}
          </FormControl>
        );
      case 'multi-select': {
        const selectedValues = Array.isArray(value) ? value.map((item) => String(item)) : [];
        const optionLabelMap = new Map((field.options || []).map((opt) => [String(opt.value), opt.label]));

        return (
          <FormControl fullWidth error={fieldError} size="small" sx={{ direction: resolvedDirection, textAlign: isRTL ? 'right' : 'left' }}>
            <InputLabel
              sx={{
                ...dynamicInputLabelSx,
                ...(isRTL ? {} : { left: 14, right: 'unset', transformOrigin: 'left' }),
              }}
            >
              <FieldLabel field={fieldWithDirection} />
            </InputLabel>
            <MuiSelect
              multiple
              value={selectedValues}
              onChange={(e) => handleChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              label={field.label}
              disabled={fieldDisabled}
              IconComponent={() => <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.8rem', marginLeft: 12, color: '#94a3b8' }} />}
              sx={{
                ...dynamicSelectSx,
                ...(isRTL
                  ? {}
                  : {
                      '& .MuiSelect-select': {
                        paddingLeft: '14px !important',
                        paddingRight: '40px !important',
                        textAlign: 'left',
                        direction: 'ltr',
                      },
                      '& .MuiSelect-icon': { right: 12, left: 'unset' },
                    }),
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, py: 0.5 }}>
                  {selected.map((selectedValue) => (
                    <Chip
                      key={selectedValue}
                      label={optionLabelMap.get(String(selectedValue)) || String(selectedValue)}
                      size="small"
                      sx={{ height: 26, borderRadius: '999px' }}
                    />
                  ))}
                </Box>
              )}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                transformOrigin: { vertical: 'top', horizontal: 'right' },
                PaperProps: {
                  sx: {
                    ...zainOverrideSx,
                    borderRadius: '12px',
                    mt: 1,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    direction: resolvedDirection
                  }
                },
              }}
            >
              {(field.options || []).map((opt) => {
                const optionValue = String(opt.value);
                return (
                  <MenuItem
                    key={optionValue}
                    value={optionValue}
                    sx={{
                      ...dynamicRtlSx,
                      direction: resolvedDirection,
                      textAlign: isRTL ? 'right' : 'left',
                      '&:hover': { backgroundColor: '#f1f5f9' },
                    }}
                  >
                    <Checkbox checked={selectedValues.includes(optionValue)} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                );
              })}
            </MuiSelect>
            {helperText && (
              <FormHelperText sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </FormControl>
        );
      }
      case 'switch':
        return (
          <FormControl fullWidth size="small" sx={{ direction: resolvedDirection, textAlign: isRTL ? 'right' : 'left' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(value)}
                  onChange={(e) => handleChange(field.name, e.target.checked)}
                  onBlur={() => handleBlur(field.name)}
                  disabled={fieldDisabled}
                  color="primary"
                />
              }
              label={<FieldLabel field={fieldWithDirection} />}
              sx={{
                ...dynamicRtlSx,
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontFamily: ZAIN,
                  fontWeight: 600,
                  color: '#475569',
                  ml: isRTL ? 0 : 1,
                  mr: isRTL ? 1 : 0
                }
              }}
            />
            {helperText && (
              <FormHelperText error sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </FormControl>
        );
      case 'select':
        return (
          <Box>
            <SelectField
              field={fieldWithDirection}
              value={value}
              onChange={(val) => handleChange(field.name, val)}
              onBlur={() => handleBlur(field.name)}
              error={fieldError}
              disabled={fieldDisabled}
            />
            {helperText && (
              <FormHelperText sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </Box>
        );
      case 'async-select':
        return (
          <Box>
            <AsyncSelectField
              field={fieldWithDirection}
              value={value}
              onChange={(val) => handleChange(field.name, val)}
              onBlur={() => handleBlur(field.name)}
              error={fieldError}
              disabled={fieldDisabled}
            />
            {helperText && (
              <FormHelperText error sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </Box>
        );
      case 'file':
        return (
          <Box>
            <FileField
              field={fieldWithDirection}
              value={value}
              onChange={(file) => handleChange(field.name, file)}
              onBlur={() => handleBlur(field.name)}
              error={fieldError}
              disabled={fieldDisabled}
            />
            {field.renderExtra && field.renderExtra(formData)}
            {helperText && (
              <FormHelperText error sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </Box>
        );
      case 'time':
        return (
          <Suspense fallback={<MiniLoader size={18} color={PRIMARY_COLOR} speed="normal" />}>
            <TimeComponent
              label={<FieldLabel field={field} />}
              value={value}
              onChange={(val) => handleChange(field.name, val)}
              onBlur={(val) => handleBlur(field.name, val)}
              error={fieldError}
              helperText={helperText}
              inputLabelSx={{
                ...dynamicInputLabelSx,
                ...(isRTL ? {} : { left: 14, right: 'unset', transformOrigin: 'left' }),
              }}
              commonInputSx={dynamicCommonInputSx}
              icon={field.icon}
              disabled={fieldDisabled}
              useSimpleTime={field.useSimpleTime}
            />
          </Suspense>
        );

      case 'date':
      case 'datetime-local':
        return (
          <Suspense fallback={<MiniLoader size={18} color={PRIMARY_COLOR} speed="normal" />}>
            <DateComponent
              label={<FieldLabel field={field} />}
              value={value}
              type={field.type}
              onChange={(val) => handleChange(field.name, val)}
              onBlur={(val) => handleBlur(field.name, val)}
              error={fieldError}
              helperText={helperText}
              inputLabelSx={{
                ...dynamicInputLabelSx,
                ...(isRTL ? {} : { left: 14, right: 'unset', transformOrigin: 'left' }),
              }}
              commonInputSx={dynamicCommonInputSx}
              icon={field.icon}
              disabled={fieldDisabled}
            />
          </Suspense>
        );

      case 'checkbox':
        return (
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => handleChange(field.name, e.target.checked)}
                  disabled={fieldDisabled}
                  sx={{
                    color: '#94a3b8',
                    '&.Mui-checked': { color: PRIMARY_COLOR },
                  }}
                />
              }
              label={<FieldLabel field={fieldWithDirection} />}
              sx={{
                margin: 0,
                direction: resolvedDirection,
                ...dynamicRtlSx,
                '& .MuiFormControlLabel-label': {
                  fontFamily: ZAIN,
                  fontWeight: 500,
                  ml: isRTL ? 0 : 1,
                  mr: isRTL ? 1 : 0,
                }
              }}
            />
            {helperText && (
              <FormHelperText error sx={{ ...dynamicRtlSx, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                {helperText}
              </FormHelperText>
            )}
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </Box>
        );

      default:
        return (
          <Box>
            <TextField
              label={<FieldLabel field={fieldWithDirection} />}
              type={field.type === 'password' ? (passwordVisibility[field.name] ? 'text' : 'password') : (field.type || 'text')}
              value={value}
              onChange={(e) => handleChange(field.name, e.target.value)}
              onBlur={() => handleBlur(field.name)}
              error={fieldError}
              helperText={helperText ? (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
                  {helperText}
                </Box>
              ) : null}
              fullWidth
              size="small"
              multiline={Boolean(field.multiline)}
              minRows={field.multiline ? (field.rows || 3) : undefined}
              disabled={fieldDisabled}
              sx={dynamicCommonInputSx}
              InputLabelProps={{
                sx: {
                  ...dynamicInputLabelSx,
                  ...(isRTL ? {} : { left: 14, right: 'unset', transformOrigin: 'left' }),
                },
                shrink: field.type === 'time' ? true : undefined,
              }}
              FormHelperTextProps={{ sx: { textAlign: isRTL ? 'right' : 'left', fontFamily: ZAIN } }}
              InputProps={
                field.type === 'password'
                  ? {
                      endAdornment: (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPasswordVisibility((prev) => ({
                              ...prev,
                              [field.name]: !prev[field.name],
                            }));
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ ml: 1 }}
                        >
                          <FontAwesomeIcon icon={passwordVisibility[field.name] ? faEyeSlash : faEye} />
                        </IconButton>
                      )
                    }
                  : undefined
              }
              inputProps={{
                maxLength: field.max,
                autoComplete: 'new-password',
                style: {
                  direction: resolvedDirection,
                  textAlign: isRTL ? 'right' : 'left',
                },
              }}
            />
            {renderNote && (
              <FormHelperText
                sx={{
                  ...dynamicRtlSx,
                  mt: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  color: '#854d0e',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  fontWeight: 700,
                }}
              >
                {helperNote}
              </FormHelperText>
            )}
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 400 }}
      sx={zainOverrideSx}
      PaperProps={{
        sx: {
          ...zainOverrideSx,
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          direction: resolvedDirection,
          overflow: 'hidden'
        }
      }}
      dir={resolvedDirection}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          py: 3,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(to bottom, rgba(122, 106, 216, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
        }}
      >
        <Typography sx={{ fontFamily: ZAIN, fontWeight: 800, fontSize: '1.25rem', color: '#1e293b' }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#94a3b8',
            transition: 'all 0.2s',
            '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', transform: 'rotate(90deg)' }
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent
        sx={{
          px: 4,
          py: 2,
          // hide scrollbar but keep scroll functional
          overflowY: 'auto',
          scrollbarWidth: 'none',          // Firefox
          '&::-webkit-scrollbar': { display: 'none' },  // Chrome / Safari
          msOverflowStyle: 'none',         // IE / Edge
        }}
      >
        <Box sx={{ mt: 1 }}>
          {formConfig.map((field, idx) => (
            <Fade in={isOpen} style={{ transitionDelay: `${idx * 50}ms` }} key={field.name}>
              <Box sx={{ marginBottom: 3 }}>
                {renderField(field)}
              </Box>
            </Fade>
          ))}
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 4, py: 3, gap: 1.5, borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: ZAIN,
            color: '#64748b',
            px: 3,
            borderRadius: '10px',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#e2e8f0', color: '#334155' }
          }}
        >
          {t('cancel') || 'إلغاء'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={
            loading
              ? <MiniLoader size={16} color="#ffffff" variant="dots" />
              : <FontAwesomeIcon icon={faPaperPlane} />
          }
          sx={{
            fontFamily: ZAIN,
            px: 4,
            py: 1,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            background: PRIMARY_GRADIENT,
            boxShadow: '0 4px 12px rgba(122, 106, 216, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: PRIMARY_GRADIENT,
              boxShadow: '0 6px 20px rgba(122, 106, 216, 0.4)',
              transform: 'translateY(-2px)'
            },
            '&.Mui-disabled': { opacity: 0.7, color: '#fff' },
            gap: 1.5,
            '& .MuiButton-startIcon': { margin: 0, ml: isRTL ? 0.5 : 'unset', mr: isRTL ? 'unset' : 0.5 },
          }}
        >
          {loading ? (t('sendingData') || 'جاري الإرسال...') : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DynamicModal;